import React, { useEffect, useState, useMemo } from "react";
import { axiosInstance as axios } from "../utils/axios";
import { PROFILE_IMG_FALLBACK } from "../config.common";

/**
 * FbsStaffSwitcher — Reusable modal for switching staff on a tablet kiosk.
 *
 * Props:
 *  - isOpen        : boolean — controls visibility
 *  - onClose       : () => void
 *  - orgId         : number|string — organization id to fetch staff from
 *  - locationId    : number|string — FBS location id
 *  - currentStaffId: number|null — currently associated staff id (to highlight)
 *  - onSwitchSuccess: (locationData) => void — called after successful switch
 */
export default function FbsStaffSwitcher({
    isOpen,
    onClose,
    orgId,
    locationId,
    currentStaffId,
    onSwitchSuccess,
}) {
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [confirmStaff, setConfirmStaff] = useState(null); // staff object pending confirmation
    const [switchPassword, setSwitchPassword] = useState("");
    const [switching, setSwitching] = useState(false);
    const [sendingReset, setSendingReset] = useState(false);
    const [resetMessage, setResetMessage] = useState("");
    const [error, setError] = useState(null);

    /* Fetch staff list when modal opens */
    useEffect(() => {
        if (isOpen && orgId) {
            fetchStaffList();
        }
        if (!isOpen) {
            setSearchText("");
            setConfirmStaff(null);
            setSwitchPassword("");
            setResetMessage("");
            setError(null);
        }
    }, [isOpen, orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchStaffList = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get("fbs/staff-list", {
                params: { org_id: orgId },
            });
            setStaffList(response?.data?.staff ?? []);
        } catch (e) {
            console.error("Failed to fetch staff list", e);
            setError("Could not load staff list.");
        } finally {
            setLoading(false);
        }
    };

    /* Client-side search filtering */
    const filteredStaff = useMemo(() => {
        const staffWithUnassigned = [
            {
                id: 0,
                username: "Unassigned",
                fname: "",
                lname: "",
                email: "",
                is_unassigned: true,
            },
            ...staffList,
        ];
        if (!searchText.trim()) return staffWithUnassigned;
        const q = searchText.toLowerCase();
        return staffWithUnassigned.filter(
            (s) =>
                (s.fname ?? "").toLowerCase().includes(q) ||
                (s.lname ?? "").toLowerCase().includes(q) ||
                (s.username ?? "").toLowerCase().includes(q) ||
                (s.email ?? "").toLowerCase().includes(q),
        );
    }, [staffList, searchText]);

    /* Handle switch call */
    const doSwitch = async (staff) => {
        setSwitching(true);
        setError(null);
        try {
            const payload = staff?.is_unassigned
                ? {
                      location_id: locationId,
                      unassign: true,
                  }
                : {
                      location_id: locationId,
                      staff_id: staff.id,
                      switch_password: switchPassword,
                  };
            const response = await axios.post("fbs/switch-staff", payload);
            setConfirmStaff(null);
            setSwitchPassword("");
            onClose();
            if (onSwitchSuccess) onSwitchSuccess(response?.data?.location);
        } catch (e) {
            console.error("Staff switch failed", e);
            setError(
                e?.response?.data?.messages?.[0] ?? "Failed to switch staff.",
            );
        } finally {
            setSwitching(false);
        }
    };

    const requestPasswordReset = async (staff) => {
        if (!staff?.id) return;
        setSendingReset(true);
        setError(null);
        setResetMessage("");
        try {
            await axios.post("fbs/staff-switch-password/reset/request", {
                org_id: orgId,
                user_id: staff.id,
            });
            setResetMessage(
                `Reset link sent to ${staff.email || "the selected user email"}.`,
            );
        } catch (e) {
            console.error("Password reset request failed", e);
            setError(
                e?.response?.data?.messages?.[0] ??
                    "Could not send password reset link.",
            );
        } finally {
            setSendingReset(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Main Staff List Modal */}
            <div className="fbs-staff-switcher-overlay" onClick={onClose}>
                <div
                    className="fbs-staff-switcher-modal"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="fbs-staff-switcher-header">
                        <h3 style={{ margin: 0 }}>Switch Staff on Duty</h3>
                        <button
                            className="fbs-staff-switcher-close"
                            onClick={onClose}
                        >
                            &times;
                        </button>
                    </div>

                    <input
                        type="text"
                        placeholder="Search staff by username…"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="fbs-staff-switcher-search"
                        autoFocus
                    />

                    {error && (
                        <p
                            style={{
                                color: "#c62828",
                                padding: "0 16px",
                                fontSize: 14,
                            }}
                        >
                            {error}
                        </p>
                    )}

                    <div className="fbs-staff-switcher-list">
                        {loading ? (
                            <p style={{ textAlign: "center", padding: 24 }}>
                                Loading…
                            </p>
                        ) : filteredStaff.length === 0 ? (
                            <p
                                style={{
                                    textAlign: "center",
                                    padding: 24,
                                    opacity: 0.6,
                                }}
                            >
                                No staff found
                            </p>
                        ) : (
                            filteredStaff.map((staff) => (
                                <div
                                    key={staff.id}
                                    className={`fbs-staff-switcher-item ${staff.id === currentStaffId || (staff.is_unassigned && !currentStaffId) ? "is-current" : ""}`}
                                    onClick={() => {
                                        if (staff.id !== currentStaffId || (staff.is_unassigned && currentStaffId)) {
                                            setSwitchPassword("");
                                            setResetMessage("");
                                            setConfirmStaff(staff);
                                        }
                                    }}
                                >
                                    <img
                                        src={
                                            staff.is_unassigned
                                                ? PROFILE_IMG_FALLBACK
                                                : staff.avatar ?? PROFILE_IMG_FALLBACK
                                        }
                                        alt=""
                                        className="fbs-staff-switcher-avatar"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = PROFILE_IMG_FALLBACK;
                                        }}
                                    />
                                    <div className="fbs-staff-switcher-info">
                                        <strong>
                                            {staff.username || "Unknown username"}
                                        </strong>
                                        {staff.meta?.designation && (
                                            <small
                                                style={{
                                                    display: "block",
                                                    opacity: 0.6,
                                                }}
                                            >
                                                {staff.meta.designation}
                                            </small>
                                        )}
                                    </div>
                                    {(staff.id === currentStaffId || (staff.is_unassigned && !currentStaffId)) && (
                                        <span className="fbs-staff-switcher-badge">
                                            Current
                                        </span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Confirmation Dialog */}
            {confirmStaff && (
                <div
                    className="fbs-staff-switcher-overlay"
                    style={{ zIndex: 10001 }}
                    onClick={() => setConfirmStaff(null)}
                >
                    <div
                        className="fbs-staff-switcher-confirm"
                        style={{ position: "relative" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {!confirmStaff?.is_unassigned && (
                            <button
                                type="button"
                                onClick={() => requestPasswordReset(confirmStaff)}
                                disabled={sendingReset || switching}
                                style={{
                                    position: "absolute",
                                    top: 12,
                                    right: 12,
                                    border: "1px solid #1976d2",
                                    background: "#fff",
                                    color: "#1565c0",
                                    borderRadius: 8,
                                    padding: "6px 10px",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor:
                                        sendingReset || switching
                                            ? "default"
                                            : "pointer",
                                }}
                            >
                                {sendingReset ? "Sending..." : "Reset Password"}
                            </button>
                        )}
                        <img
                            src={confirmStaff.avatar ?? PROFILE_IMG_FALLBACK}
                            alt=""
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = PROFILE_IMG_FALLBACK;
                            }}
                            style={{
                                width: 80,
                                height: 80,
                                borderRadius: "50%",
                                objectFit: "cover",
                                margin: "0 auto 12px",
                                display: "block",
                            }}
                        />
                        <h4 style={{ margin: "0 0 4px", textAlign: "center" }}>
                            {confirmStaff.username || "Unknown username"}
                        </h4>
                        <p
                            style={{
                                textAlign: "center",
                                opacity: 0.7,
                                margin: "0 0 16px",
                                fontSize: 14,
                            }}
                        >
                            {confirmStaff?.is_unassigned
                                ? "Unassign the staff on duty for this location?"
                                : "Switch this person as the staff on duty?"}
                        </p>
                        {!confirmStaff?.is_unassigned && (
                            <input
                                type="password"
                                value={switchPassword}
                                onChange={(e) => setSwitchPassword(e.target.value)}
                                className="fbs-staff-switcher-search"
                                placeholder="Enter selected user's password"
                                autoFocus
                                style={{ marginBottom: 12 }}
                            />
                        )}
                        {resetMessage && (
                            <p
                                style={{
                                    color: "#1b5e20",
                                    textAlign: "center",
                                    fontSize: 13,
                                    marginTop: 0,
                                }}
                            >
                                {resetMessage}
                            </p>
                        )}
                        {error && (
                            <p
                                style={{
                                    color: "#c62828",
                                    textAlign: "center",
                                    fontSize: 13,
                                }}
                            >
                                {error}
                            </p>
                        )}
                        <div
                            style={{
                                display: "flex",
                                gap: 12,
                                justifyContent: "center",
                            }}
                        >
                            <button
                                className="fbs-staff-switcher-btn fbs-staff-switcher-btn--cancel"
                                onClick={() => setConfirmStaff(null)}
                                disabled={switching}
                            >
                                Cancel
                            </button>
                            <button
                                className="fbs-staff-switcher-btn fbs-staff-switcher-btn--confirm"
                                onClick={() => doSwitch(confirmStaff)}
                                disabled={
                                    switching ||
                                    (!confirmStaff?.is_unassigned && !switchPassword)
                                }
                            >
                                {switching ? "Switching…" : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/**
 * Checks if staff switching should be enabled.
 * Requires: template meta.staff_switchable === true AND tablet name contains "SWITCHABLE".
 */
export function isStaffSwitchEnabled(template, fbsMeta) {
    const staffSwitchable = template?.meta?.staff_switchable === true;
    const tabName = (fbsMeta?.tabName ?? "").toUpperCase();
    const hasSwitchableInName = tabName.includes("SWITCHABLE");
    return staffSwitchable && hasSwitchableInName;
}
